// Files attached to a post or an answer: picked, pasted or dropped, uploaded at
// once (the same two-step upload as documents, with an image's size so the
// thumbnail's box is known), shown as chips with their progress, and handed
// over as file ids when sent.
//
// Promise:
// - Nothing is sent while a file is still going up; taking one off aborts its
//   upload.
// - A file that cannot go up leaves the chips and says why in a toast (a chip
//   kept with its reason took the room of the text and stopped sending until
//   taken off; the person asked, 2026-09-30).
// - The chips are one row 32 px high that scrolls sideways, whatever their
//   number (wrapped, a few images pushed the text above the keyboard on a
//   phone); adding scrolls to the new ones. An image is its small thumbnail
//   (from the file in hand, before it is up, a little faded until then), its
//   number when the text names it at its bottom left, and × over its top
//   right corner; its name is read aloud only. A video is the same small
//   thumbnail (its first frame) with ▶. Other files show the name and the
//   size. Progress is a thin line growing, not a number. Pressing a thumbnail
//   shows the image large, or plays the video in the full-screen viewer.

import { isVideoFileMime } from "@trameria/core/files-shared";
import { m } from "@trameria/i18n";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useApi, useMeApp } from "../app/context";
import {
  FileLightbox,
  openFileLightbox,
  VideoViewer,
} from "../documents/blocks/media";
import {
  fileUrl,
  formatBytes,
  imageDimensions,
  sha256Hex,
  type UploadHandle,
  uploadAborted,
  uploadErrorMessage,
  uploadFile,
  uploadMimeOf,
  videoDimensions,
} from "../documents/blocks/upload";
import type { RestoredFile } from "../me/draft";
import { Icon } from "../ui/icon";
import { toast } from "../ui/toast";
import { LINKED_OUTLINE, VideoStill } from "./attachments";
import { isImage } from "./image-labels";
import { useSigner } from "./signing";

/**
 * The SHA-256 of the files this screen put up, by id: what a signed post or
 * answer names its files by (./signing.ts). A file put up before (a draft
 * kept from an earlier visit) is hashed when the draft is put back (`restore`).
 */
const fileHashesById = new Map<string, string>();

export function rememberFileHash(id: string, hash: string): void {
  fileHashesById.set(id, hash);
}

export const fileHashOf = (id: string): string | null =>
  fileHashesById.get(id) ?? null;

export interface Upload {
  key: string;
  name: string;
  mime: string;
  size: number;
  progress: number;
  fileId: string | null;
  /**
   * An image's or a video's picture from the file in hand (an object URL),
   * for the chip and the viewer before it is up. null for other files. Let go
   * when the file is taken off or sent.
   */
  preview: string | null;
  /** An image's or a video's size, measured before it goes up. */
  width: number | null;
  height: number | null;
}

export interface Uploads {
  items: readonly Upload[];
  /**
   * Attaches files (as many as there is room for). `onDropped` hears of one
   * that could not go up and left, with its number among the images (null
   * for another file), so the text can drop its label.
   */
  add(
    files: readonly File[],
    options?: { onDropped?(imageNumber: number | null): void },
  ): void;
  remove(key: string): void;
  clear(): void;
  /**
   * Puts back files already up (a draft read back), in place of what is
   * attached: uploads still going stop, images show from their stored file.
   * `before`: put before what is attached instead (a post taken back), as
   * many as there is room for.
   */
  restore(files: readonly RestoredFile[], options?: { before?: boolean }): void;
  /** Files ready to send (in the order they were added). */
  ready(): Upload[];
  busy: boolean;
  /** How many more files `add` takes now (the rest are dropped). */
  room: number;
}

let seq = 0;

export function useUploads(max: number): Uploads {
  const api = useApi();
  const { apiBase } = useMeApp();
  // Only a screen that signs needs the hashes (a browser only reads).
  const signs = useSigner() !== null;
  const [items, setItems] = useState<Upload[]>([]);
  const handles = useRef(new Map<string, { abort(): void }>());
  const patch = useCallback(
    (key: string, p: Partial<Upload>) =>
      setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x))),
    [],
  );
  const current = useRef(items);
  current.current = items;
  const add = useCallback<Uploads["add"]>(
    (files, options) => {
      const room = Math.max(0, max - current.current.length);
      const next = files.slice(0, room).map((file): Upload => {
        const key = `u${++seq}`;
        const mime = uploadMimeOf(file);
        const preview = shownAsPicture(mime) ? URL.createObjectURL(file) : null;
        let handle: UploadHandle | null = null;
        let aborted = false;
        handles.current.set(key, {
          abort: () => {
            aborted = true;
            handle?.abort();
          },
        });
        void (async () => {
          const size =
            (await imageDimensions(file)) ??
            (await videoDimensions(file, mime));
          // Taken off while it was measured: nothing goes up.
          if (aborted) return;
          if (size) patch(key, size);
          handle = uploadFile(api, file, {
            onProgress: (r) => patch(key, { progress: r }),
            dimensions: size,
          });
          try {
            const f = await handle.promise;
            handles.current.delete(key);
            rememberFileHash(f.id, f.hash);
            patch(key, { fileId: f.id, progress: 1 });
          } catch (error) {
            handles.current.delete(key);
            const text = uploadErrorMessage(error);
            if (aborted || text === uploadAborted()) return;
            toast(
              m.agents_attachment_failed({ name: file.name, reason: text }),
            );
            const images = current.current.filter((x) => isImage(x.mime));
            const n = images.findIndex((x) => x.key === key);
            if (preview) URL.revokeObjectURL(preview);
            current.current = current.current.filter((x) => x.key !== key);
            setItems((xs) => xs.filter((x) => x.key !== key));
            options?.onDropped?.(isImage(mime) && n >= 0 ? n + 1 : null);
          }
        })();
        return {
          key,
          name: file.name,
          mime,
          size: file.size,
          progress: 0,
          fileId: null,
          preview,
          width: null,
          height: null,
        };
      });
      current.current = [...current.current, ...next];
      setItems((xs) => [...xs, ...next]);
    },
    [api, max, patch],
  );
  const remove = useCallback((key: string) => {
    handles.current.get(key)?.abort();
    handles.current.delete(key);
    const gone = current.current.find((x) => x.key === key);
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    current.current = current.current.filter((x) => x.key !== key);
    setItems((xs) => xs.filter((x) => x.key !== key));
  }, []);
  const clear = useCallback(() => {
    for (const h of handles.current.values()) h.abort();
    handles.current.clear();
    for (const x of current.current)
      if (x.preview) URL.revokeObjectURL(x.preview);
    current.current = [];
    setItems([]);
  }, []);
  const restore = useCallback<Uploads["restore"]>(
    (files, options) => {
      const kept = options?.before ? current.current : [];
      if (!options?.before) clear();
      const next = files.slice(0, Math.max(0, max - kept.length)).map(
        (f): Upload => ({
          key: `u${++seq}`,
          name: f.name,
          mime: f.mime,
          size: f.size,
          // Not ready until its hash is known (below).
          progress: !signs || fileHashOf(f.id) ? 1 : 0,
          fileId: f.id,
          preview: shownAsPicture(f.mime) ? fileUrl(apiBase, f.id) : null,
          width: f.width,
          height: f.height,
        }),
      );
      current.current = [...next, ...kept];
      setItems(current.current);
      // A file put up before this page (another visit, the page read again)
      // has no hash here: it is hashed from the body this screen reads, the
      // one it shows, so the write can be signed.
      for (const x of next) {
        if (x.progress === 1 || !x.fileId) continue;
        const { key, fileId } = x;
        const ac = new AbortController();
        handles.current.set(key, { abort: () => ac.abort() });
        void (async () => {
          try {
            const res = await fetch(fileUrl(apiBase, fileId), {
              credentials: "include",
              signal: ac.signal,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const hash = await sha256Hex(
              await res.blob(),
              () => ac.signal.aborted,
            );
            handles.current.delete(key);
            rememberFileHash(fileId, hash);
            patch(key, { progress: 1 });
          } catch (error) {
            handles.current.delete(key);
            if (ac.signal.aborted) return;
            toast(
              m.agents_attachment_failed({
                name: x.name,
                reason: uploadErrorMessage(error),
              }),
            );
            current.current = current.current.filter((y) => y.key !== key);
            setItems((xs) => xs.filter((y) => y.key !== key));
          }
        })();
      }
    },
    [apiBase, clear, max, patch, signs],
  );
  // Leaving the box with files not sent: their uploads stop (quietly) and the
  // pictures are let go.
  // frontend-development: not a fetch; lets go of what the box held when it leaves.
  useEffect(() => clear, [clear]);
  const ready = useCallback(
    () => items.filter((x) => x.fileId !== null),
    [items],
  );
  return {
    items,
    add,
    remove,
    clear,
    restore,
    ready,
    busy: items.some((x) => x.fileId === null || x.progress < 1),
    room: Math.max(0, max - items.length),
  };
}

/** The chips of the attached files, under the text being written. */
export function UploadChips({
  uploads,
  numbers,
  linked = null,
  onRemove = uploads.remove,
  className = "px-3 pb-2",
}: {
  uploads: Uploads;
  /** Each image's number, when the text names the images ("image N"). */
  numbers?: ReadonlyMap<string, number>;
  /** The image whose label in the text is hovered (outlined). */
  linked?: string | null;
  /** Taking a file off (the post box also drops its label). */
  onRemove?(key: string): void;
  /** Around the row. */
  className?: string;
}) {
  const list = useRef<HTMLUListElement>(null);
  const count = useRef(uploads.items.length);
  // Added files come into sight at the end; taking one off moves nothing.
  // frontend-development: not a fetch; scrolls the row of chips to what was just added.
  useLayoutEffect(() => {
    const el = list.current;
    if (el && uploads.items.length > count.current)
      el.scrollLeft = el.scrollWidth;
    count.current = uploads.items.length;
  }, [uploads.items.length]);
  if (uploads.items.length === 0) return null;
  return (
    <>
      {/* The large view's host, for a box with no message on screen (a new chat). */}
      {uploads.items.some((u) => u.preview && isImage(u.mime)) && (
        <FileLightbox />
      )}
      {uploads.items.some((u) => u.preview && !isImage(u.mime)) && (
        <VideoViewer />
      )}
      <ul
        ref={list}
        aria-label={m.agents_attachments()}
        // Room above and at the end for the × over an image's corner (the
        // row scrolls, so it would cut it off).
        className={`flex items-end gap-2.5 overflow-x-auto overscroll-x-contain pt-1.5 [scrollbar-width:thin] ${className}`}
      >
        {uploads.items.map((u) => (
          <UploadChip
            key={u.key}
            upload={u}
            number={numbers?.get(u.key)}
            linked={linked === u.key}
            onRemove={onRemove}
          />
        ))}
      </ul>
    </>
  );
}

/** The thin line of an upload's progress (until it is up). */
function Progress({ upload: u }: { upload: Upload }) {
  if (u.fileId !== null) return null;
  return (
    // Inside the chip: along the whole bottom edge its ends went square at
    // the rounded corners.
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-1.5 bottom-[2px] h-[2px]"
    >
      <span
        className="block h-full rounded-full bg-accent transition-[width] duration-150"
        style={{ width: `${Math.round(u.progress * 100)}%` }}
      />
    </span>
  );
}

function UploadChip({
  upload: u,
  number,
  linked,
  onRemove,
}: {
  upload: Upload;
  number: number | undefined;
  linked: boolean;
  onRemove(key: string): void;
}) {
  if (u.preview)
    return (
      <ImageChip
        upload={u}
        number={number}
        linked={linked}
        onRemove={onRemove}
      />
    );
  return (
    <li
      data-linked={linked ? "" : undefined}
      className={`relative flex h-8 max-w-[220px] flex-none items-center gap-1.5 overflow-hidden rounded-[6px] border border-border pr-0.5 pl-2.5 text-[12.5px] text-fg ${LINKED_OUTLINE}`}
    >
      <span className="min-w-0 truncate">
        {u.name} <span className="text-faint">{formatBytes(u.size)}</span>
      </span>
      <button
        type="button"
        aria-label={m.agents_attachment_remove({ name: u.name })}
        onClick={() => onRemove(u.key)}
        className="flex h-6 w-6 flex-none cursor-pointer items-center justify-center rounded-[4px] text-muted hover:bg-hover hover:text-fg"
      >
        <Icon kind="close" size={12} />
      </button>
      <Progress upload={u} />
    </li>
  );
}

/**
 * An image or a video: its thumbnail (pressed, shown large or played), the N of "image N" in the
 * text at its bottom left, and × on its top right corner, over the edge (the
 * person, 2026-10-18: "the × at the top right"). The × is small to see and
 * larger to press.
 */
function ImageChip({
  upload: u,
  number,
  linked,
  onRemove,
}: {
  upload: Upload;
  number: number | undefined;
  linked: boolean;
  onRemove(key: string): void;
}) {
  const faded = u.fileId ? "opacity-100" : "opacity-60";
  return (
    <li
      data-linked={linked ? "" : undefined}
      className={`relative size-8 flex-none rounded-[6px] last:mr-1.5 ${LINKED_OUTLINE}`}
    >
      {isImage(u.mime) ? (
        <button
          type="button"
          aria-label={m.blocks_file_enlarge_named({ name: u.name })}
          onClick={(e) =>
            openFileLightbox(e.currentTarget, {
              src: u.preview as string,
              name: u.name,
            })
          }
          className="block size-full cursor-zoom-in overflow-hidden rounded-[6px] border border-border bg-border-soft"
        >
          <img
            src={u.preview as string}
            alt=""
            // Faded until it is up (with the progress line: "not yet").
            className={`pointer-events-none h-full w-full object-cover transition-opacity duration-150 ${faded}`}
          />
        </button>
      ) : (
        <VideoStill
          src={u.preview as string}
          name={u.name}
          compact
          className="relative block size-full overflow-hidden rounded-[6px] border border-border bg-black p-0"
          videoClassName={`object-cover transition-opacity duration-150 ${faded}`}
        />
      )}
      {number !== undefined && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[2px] left-[2px] flex h-3.5 min-w-3.5 items-center justify-center rounded-[4px] bg-surface/90 px-0.5 text-[10px] leading-none text-muted tabular-nums"
        >
          {number}
        </span>
      )}
      <button
        type="button"
        aria-label={m.agents_attachment_remove({ name: u.name })}
        onClick={() => onRemove(u.key)}
        className="absolute -top-1.5 -right-1.5 flex size-[18px] cursor-pointer items-center justify-center rounded-full bg-fg text-surface ring-2 ring-surface before:absolute before:-inset-1.5 before:content-[''] hover:bg-body"
      >
        <Icon kind="close" size={9} />
      </button>
      <Progress upload={u} />
    </li>
  );
}

/** Shown as its own picture in the chips (an image or a video). */
const shownAsPicture = (mime: string): boolean =>
  isImage(mime) || isVideoFileMime(mime);

/** Files among the items of a clipboard or a drop. */
export function filesOf(data: DataTransfer | null): File[] {
  if (!data) return [];
  return [...data.files];
}
